
import { Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Discount } from "@/types/discount";
import { EditDiscountDialog } from "./EditDiscountDialog";
import { useState } from "react";
import { formatPrice } from "@/components/subscriptions/utils";
import { SearchBar } from "@/components/users/table/SearchBar";
import { Pagination } from "@/components/users/table/Pagination";

interface DiscountsTableProps {
  discounts: Discount[];
  loading: boolean;
  onDelete: (discount: Discount) => void;
  onDiscountUpdated: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const DiscountsTable = ({
  discounts,
  loading,
  onDelete,
  onDiscountUpdated,
  searchQuery,
  onSearchChange,
  page,
  totalPages,
  onPageChange,
}: DiscountsTableProps) => {
  const [discountToEdit, setDiscountToEdit] = useState<Discount | null>(null);

  if (loading) {
    return <div className="min-h-[200px] flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="relative space-y-4">
      <SearchBar searchQuery={searchQuery} onSearchChange={onSearchChange} />

      <div className="overflow-x-auto">
        <div className="min-w-full inline-block align-middle">
          <div className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">School</TableHead>
                  <TableHead className="w-[150px]">Product</TableHead>
                  <TableHead className="w-[150px]">Plan</TableHead>
                  <TableHead className="w-[100px]">Type</TableHead>
                  <TableHead className="w-[120px]">Value</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {discounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center h-32">
                      {searchQuery ? "No matching discounts found" : "No discounts found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  discounts.map((discount) => (
                    <TableRow key={discount.id}>
                      <TableCell className="truncate max-w-[200px]">
                        {discount.school_name || '-'}
                      </TableCell>
                      <TableCell className="truncate max-w-[150px]">
                        {discount.product_name || '-'}
                      </TableCell>
                      <TableCell className="truncate max-w-[150px]">
                        {discount.plan_name || '-'}
                      </TableCell>
                      <TableCell>{discount.discount_type}</TableCell>
                      <TableCell>
                        {discount.discount_type === 'percentage' 
                          ? `${discount.discount_value}%` 
                          : formatPrice(discount.discount_value)}
                      </TableCell>
                      <TableCell>{discount.status}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDiscountToEdit(discount)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDelete(discount)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        loading={loading}
        onPageChange={onPageChange}
      />

      <EditDiscountDialog
        discount={discountToEdit}
        open={!!discountToEdit}
        onOpenChange={(open) => !open && setDiscountToEdit(null)}
        onDiscountUpdated={onDiscountUpdated}
      />
    </div>
  );
};
